import { useMemo } from 'react';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useFundContext from '@/contexts/Fund/useFundContext';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import useFundAccess from '@/hooks/useFundAccess';
import {
  SupportedIsraeliIdChangeRequestDtoIn,
  SUPPORTED_ID_CHANGE_REQUESTS_FEATURE_KEY,
} from '@/models/in/SupportedIsraeliIdChangeRequestDtoIn';

/** The query key of the fund's open ID change requests - invalidate it after sending, withdrawing or «הבנתי». */
export const fundIsraeliIdChangeRequestsUrl = (fundId: number) => `funds/${fundId}/israeli-id-change-requests`;

interface FundIsraeliIdChangeRequests {
  isOn: boolean;
  /** The open request of each supported, by contact id: waiting for the office, or decided and not yet seen. */
  bySupported: Map<number, SupportedIsraeliIdChangeRequestDtoIn>;
  /** Open requests - waiting, or decided and not yet seen: the count on «נתמכים» and on the board's chip. */
  openCount: number;
}

/**
 * HAT-1428: a coordinator's request to correct a supported's Israeli ID is rare, so it gets no page
 * of its own. What there is to know shows on the supported - a dot in the list, a notice in its
 * details - and on «נתמכים» as a count of open requests, until the coordinator acknowledges the
 * decision. One query for all of them, shared through the react-query cache.
 */
export default function useFundIsraeliIdChangeRequests(): FundIsraeliIdChangeRequests {
  const { currentFund } = useFundContext();
  // The requests are of the supports world: closed to a coordinator, they are not asked for.
  const { canView } = useFundAccess(currentFund?.id);
  const isOn = useFeature(SUPPORTED_ID_CHANGE_REQUESTS_FEATURE_KEY) && canView('Supports');
  const { data } = useGetAuthApi<SupportedIsraeliIdChangeRequestDtoIn[]>(
    isOn && currentFund ? fundIsraeliIdChangeRequestsUrl(currentFund.id) : '',
    undefined,
    { staleTime: 30000, refetchInterval: 120000 }
  );

  return useMemo(() => {
    const bySupported = new Map<number, SupportedIsraeliIdChangeRequestDtoIn>();
    // Newest first from the server, so the first seen for a supported is its latest
    for (const request of data ?? []) {
      if (!bySupported.has(request.contactId)) bySupported.set(request.contactId, request);
    }
    return { isOn, bySupported, openCount: bySupported.size };
  }, [data, isOn]);
}
